/**
 * Wrappers finos que inyectan `db`/`drizzle` de la app web en las queries
 * local-first de la capa de organización (org_settings/campus/room). Reactivos
 * vía `createPowerSyncQuery`/`Single` (se auto-invalidan al cambiar la tabla).
 */

import type { Accessor } from "solid-js";
import {
  academyOrgQueryKeys,
  getCampusesQuery,
  getGradingModelsQuery,
  getOrgSettingsQuery,
  getRoomsByCampusQuery,
  gradingModelsQueryKeys,
} from "@space/shared";
import { createPowerSyncQuery, createPowerSyncSingle } from "@space/shared/solid";
import type { Campus, GradingModel, OrgSettings, Room } from "@space/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  return { db, drizzle, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

/** Ajustes (singleton) de una organización. */
export function useOrgSettings(orgId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncSingle<OrgSettings>({
    queryKey: () => academyOrgQueryKeys.settings(orgId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = orgId();
      return db && id ? getOrgSettingsQuery(db, id) : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Sedes de una organización. */
export function useCampuses(orgId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<Campus>({
    queryKey: () => academyOrgQueryKeys.campuses(orgId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = orgId();
      return db && id ? getCampusesQuery(db, id) : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Aulas de una sede. */
export function useRooms(campusId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<Room>({
    queryKey: () => academyOrgQueryKeys.rooms(campusId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = campusId();
      return db && id ? getRoomsByCampusQuery(db, id) : null;
    },
    enabled: () => Boolean(campusId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Modelos de calificación (sistema + personalizados) de una organización. */
export function useGradingModels(orgId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<GradingModel>({
    queryKey: () => gradingModelsQueryKeys.byOrg(orgId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = orgId();
      return db && id ? getGradingModelsQuery(db, id) : null;
    },
    enabled: () => Boolean(orgId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** Acceso de escritura (drizzle) a la capa de organización. */
export function useAcademyWrite() {
  const drizzle = useDrizzle();
  return { drizzle };
}
